import type { ENU, LatLon, LocalFrame } from '../contracts';
import { DEG2RAD, meridianRadius, primeVerticalRadius } from './math';

/**
 * Lightweight local tangent-plane frame (equirectangular with WGS84 radii at
 * the origin). Accurate to a few cm within ~1 km and ~1 m at 5 km.
 *
 * Used by the PoseEngine as a stand-in while no lake frame is active (so GPS
 * speed/course and lat/lon keep working), and by tests. Real lake frames come
 * from the geo module (exact ENU via ECEF).
 *
 * `rotationDeg` rotates the frame's +N axis clockwise away from true north
 * (tests use it to exercise LocalFrame.headingCorrectionDeg).
 */
export class TangentPlaneFrame implements LocalFrame {
  readonly origin: LatLon;
  private readonly mLat: number;
  private readonly mLon: number;
  private readonly cosR: number;
  private readonly sinR: number;
  private readonly rotationDeg: number;

  constructor(origin: LatLon, rotationDeg = 0) {
    this.origin = { lat: origin.lat, lon: origin.lon };
    this.mLat = meridianRadius(origin.lat) * DEG2RAD;
    this.mLon = primeVerticalRadius(origin.lat) * Math.cos(origin.lat * DEG2RAD) * DEG2RAD;
    this.rotationDeg = rotationDeg;
    this.cosR = Math.cos(rotationDeg * DEG2RAD);
    this.sinR = Math.sin(rotationDeg * DEG2RAD);
  }

  toENU(p: LatLon): ENU {
    const de = (p.lon - this.origin.lon) * this.mLon;
    const dn = (p.lat - this.origin.lat) * this.mLat;
    return { e: de * this.cosR - dn * this.sinR, n: de * this.sinR + dn * this.cosR };
  }

  toLatLon(p: ENU): LatLon {
    const de = p.e * this.cosR + p.n * this.sinR;
    const dn = -p.e * this.sinR + p.n * this.cosR;
    return { lat: this.origin.lat + dn / this.mLat, lon: this.origin.lon + de / this.mLon };
  }

  headingCorrectionDeg(): number {
    return -this.rotationDeg;
  }
}
